import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { LoaderService } from "./loader.service";
import moment from "moment";
import { environment } from "src/environments/environment";
import { DevicePlatform, DocumentTypeOfSummary, DownloadResponseType } from "../common.interface";
import { CommonService } from "./common.service";
import { MqttEventService } from "./mqtt-event.service";
import { InfoDialogComponent } from "../dialogs/info-dialog/info-dialog.component";

@Injectable({
    providedIn: "root",
})
export class GlobalService {
    public isReadNotification = new BehaviorSubject<boolean>(false);
    public loadIframe = new BehaviorSubject<boolean>(false);
    public showSidebar = new BehaviorSubject<boolean>(false);
    public showHeader = new BehaviorSubject<boolean>(false);
    public sidebarToggle = new BehaviorSubject<boolean>(false);
    public unreadNotificationsCount = new BehaviorSubject<number>(0);
    public rewardWalletBalance = new BehaviorSubject<number>(0);
    public globalChannel = new BroadcastChannel("auth");
    public customerDetails = new BehaviorSubject<any>({});

    constructor(
        private dialog: MatDialog,
        private router: Router,
        public loaderService: LoaderService,
        private commonService: CommonService,
        private mqttService: MqttEventService
    ) {
        const rawLeader = localStorage.getItem(this.mqttService.LEADER_KEY);
        const leader = rawLeader ? JSON.parse(rawLeader) : null;
        const tabId = sessionStorage.getItem(this.mqttService.TAB_ID_KEY);
        const isLeaderTab = leader?.tabId === tabId;
        if (rawLeader && !isLeaderTab) {
            // this.globalChannel.onmessage = (event) => {
            //     if (event.data?.source === "LOGOUT") {
            //         window.location.reload();
            //     } else if (event.data?.source === "LOGIN") {
            //         window.location.reload();
            //     }
            // };
        }
    }

    getDevicePlatform(...platforms: DevicePlatform[]): boolean {
        const ua = navigator.userAgent;
        const isAndroid = /Android/i.test(ua);
        // iPadOS 13+ reports a Mac user agent, so detect it via touch support
        const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
        // Anything that is not a mobile OS is treated as desktop / laptop
        const isWeb = !isAndroid && !isIos;

        return platforms.some((platform) => {
            switch (platform) {
                case "android":
                    return isAndroid;
                case "ios":
                    return isIos;
                case "web":
                    return isWeb;
                case "all":
                    return true;
                default:
                    return false;
            }
        });
    }

    getDeviceName(): string {
        const ua = navigator.userAgent;

        if (/iPhone/.test(ua)) return "iPhone";
        if (/iPad/.test(ua)) return "iPad";
        if (/iPod/.test(ua)) return "iPod";
        if (/Android/.test(ua)) {
            // Try to extract device model from user agent (works on some Androids)
            const match = ua.match(/Android.*;\s([^)]+)/);
            return match ? match[1].trim() : "Android Device";
        }
        if (/Windows/.test(ua)) return "Windows PC";
        if (/Macintosh/.test(ua)) return "Mac";
        if (/Linux/.test(ua)) return "Linux PC";

        return "Unknown Device";
    }

    getBrowserNameAndVersion(): { browserName: string; browserVersion: string } {
        const ua = navigator.userAgent.toLowerCase();

        const getVersion = (marker: string): string => {
            const regex = new RegExp(`${marker}(\\d+(\\.\\d+){0,3})`, "i");
            const match = ua.match(regex);
            return match ? match[1] : "Unknown";
        };

        let browserName = "Unknown";
        let browserVersion = "Unknown";

        if (ua.includes("crios")) {
            browserName = "Chrome";
            browserVersion = getVersion("crios/");
        } else if (ua.includes("edg") || ua.includes("edge")) {
            browserName = "Edge";
            browserVersion = getVersion("edg/");
        } else if (ua.includes("fxios")) {
            browserName = "Firefox";
            browserVersion = getVersion("fxios/");
        } else if (ua.includes("firefox")) {
            browserName = "Firefox";
            browserVersion = getVersion("firefox/");
        } else if (ua.includes("samsungbrowser")) {
            browserName = "Samsung Internet";
            browserVersion = getVersion("samsungbrowser/");
        } else if (ua.includes("opr")) {
            browserName = "Opera";
            browserVersion = getVersion("opr/");
        } else if (ua.includes("opera")) {
            browserName = "Opera";
            browserVersion = getVersion("opera/");
        } else if (ua.includes("msie")) {
            browserName = "Internet Explorer";
            browserVersion = getVersion("msie ");
        } else if (ua.includes("trident")) {
            browserName = "Internet Explorer";
            browserVersion = getVersion("rv:");
        } else if (ua.includes("chrome")) {
            browserName = "Chrome";
            browserVersion = getVersion("chrome/");
        } else if (ua.includes("safari")) {
            browserName = "Safari";
            browserVersion = getVersion("version/");
        }

        return { browserName, browserVersion };
    }

    internetConnection() {
        this.router.navigate(["/info"], {
            state: {
                template: "NoInternet",
            },
        });
    }

    requestLocationPermission() {
        const existingDialog = this.dialog.openDialogs.find(
            (dialogRef) => dialogRef.componentInstance instanceof InfoDialogComponent
        );

        if (existingDialog) {
            // Dialog is already open, do nothing
            return;
        }

        this.router.navigate(["/info"], {
            state: {
                template: "LocationPermission",
            },
        });
    }

    serverDown() {
        this.router.navigate(["/info"], {
            state: {
                template: "ServerDown",
            },
        });
    }

    catchError() {
        this.router.navigate(["/info"], {
            state: {
                template: "ServerError",
            },
        });
    }

    displayCommonDate(
        date: string | Date,
        options?: {
            time?: boolean;
            format?: "12hr" | "24hr";
        }
    ): string {
        const mDate = moment(date, "YYYY-MM-DD HH:mm:ss");
        const today = moment().startOf("day");
        const yesterday = moment().subtract(1, "day").startOf("day");

        let formattedDate = "";

        if (mDate.isSame(today, "day")) {
            formattedDate = "Today";
        } else if (mDate.isSame(yesterday, "day")) {
            formattedDate = "Yesterday";
        } else {
            formattedDate = mDate.format("DD MMM, YYYY");
        }

        if (options?.time) {
            const timeFormat = options.format === "24hr" ? "HH:mm" : "hh:mm A";

            formattedDate += " " + mDate.format(timeFormat);
        }

        return formattedDate;
    }

    async getBlobFromUrl(url: string): Promise<string | null> {
        if (!url) return Promise.resolve(null);

        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });
        } catch {
            return null;
        }
    }

    async openComingSoon() {
        this.router.navigate(["/coming-soon"], {
            state: {
                icon: "assets/svg/icons/rocket-gbs.svg",
                title: "We’re Launching Soon!",
                description: "Thank you for your interest — our loan services are launching soon.",
                leftButtonName: "Continue Your Journey",
                leftButtonRoute: "/auth/login",
            },
        });
    }

    openStore(type: "ios" | "android"): void {
        const params = new URLSearchParams(window.location.search);
        const utmParams = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
        const hasUtmParams = utmParams.some((param) => params.has(param));

        const storeUrl = new URL(
            type === "ios" ? environment.APP_STORE : hasUtmParams ? environment.PLAY_STORE : environment.ONELINK
        );

        if (hasUtmParams) {
            utmParams.forEach((param) => {
                const value = params.get(param);
                if (value) storeUrl.searchParams.set(param, value);
            });
        }

        window.open(storeUrl.toString(), "_blank");
    }

    async downloadSummary(
        documentType: DocumentTypeOfSummary,
        fileName: string = "file",
        paymentResponseId: string = "",
        applicationId = "",
        loanId = ""
    ): Promise<boolean> {
        try {
            const res: any = await firstValueFrom(
                this.commonService.downloadSummaryPdf(documentType, applicationId, paymentResponseId)
            );

            if (res?.success && res?.data?.responseType === DownloadResponseType.URL) {
                await this.downloadPdf(res.data.downloadUrl, fileName, "pdf", loanId);
                return true;
            }

            if (res?.success && res?.data?.responseType === DownloadResponseType.BUFFER) {
                await this.downloadPdfFromNodeBuffer(res.data.buffer.data, fileName, "pdf", loanId);
                return true;
            }

            return false;
        } catch (error) {
            console.error("Download failed", error);
            return false;
        }
    }

    async downloadPdf(url?: string, fileName: string = "file", type: string = "pdf", loanId = ""): Promise<void> {
        if (!url) return Promise.resolve();

        const safeFileName = fileName.trim().replace(/\s+/g, "_");

        const res = await fetch(url);
        const blob = await res.blob();

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.download = loanId ? `${safeFileName}_${loanId}.${type}` : `${safeFileName}.${type}`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        return Promise.resolve();
    }

    async downloadPdfFromNodeBuffer(
        bufferData: number[],
        fileName: string = "file",
        type: string = "pdf",
        loanId = ""
    ): Promise<void> {
        const safeFileName = fileName.trim().replace(/\s+/g, "_");

        const uint8Array = new Uint8Array(bufferData);
        const blob = new Blob([uint8Array], { type: "application/pdf" });

        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.download = loanId ? `${safeFileName}_${loanId}.${type}` : `${safeFileName}.${type}`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        return Promise.resolve();
    }
}
